'use client';
import FormControlLabel, {
  FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import { useFormikContext } from 'formik';
import { Checkbox, Stack, Typography, useTheme } from '@mui/material';
import get from 'lodash/get';
import useIsMobile from '../../utils/useIsMobile';
import { useDisabled } from './providers/DisableProvider';
import Optional from '../Optional';

type Props = Omit<FormControlLabelProps, 'control'> & {
  name: string;
  helperText?: string;
  errorGutterBottom?: boolean;
};

const CheckboxInput = ({
  name,
  helperText,
  errorGutterBottom,
  ...props
}: Props) => {
  const { setFieldValue, setFieldTouched, values, touched, errors } =
    useFormikContext<any>();
  const isError = !!(get(touched, name) && get(errors, name));
  const isMobile = useIsMobile();
  const { getDisabled } = useDisabled();
  const theme = useTheme();
  const errorSize =
    parseFloat(theme.typography.body2.fontSize as string) - (isMobile ? 3 : 5);

  return (
    <Stack>
      <FormControlLabel
        sx={{
          paddingBottom: errorGutterBottom
            ? isError
              ? 0
              : `${errorSize}px`
            : 0,
        }}
        disabled={getDisabled?.(name)}
        {...props}
        name={name}
        value={get(values, name) || false}
        onBlur={(event) => {
          props.onBlur?.(event);
          setFieldTouched(name, true);
        }}
        onChange={(event, value) => {
          props.onChange?.(event, value);
          setFieldValue(name, value);
        }}
        control={
          <Checkbox
            size={isMobile ? 'small' : 'medium'}
            checked={get(values, name) || false}
          />
        }
      />
      <Optional condition={isError || !!helperText}>
        <Typography
          sx={{
            lineHeight: 1,
            textTransform: 'uppercase',
            letterSpacing: 2,
            fontWeight: 600,
            mt: '-5px',
            color: isError ? 'error.main' : 'text.secondary',
          }}
          color={isError ? 'error' : 'textSecondary'}
          variant={'body2'}
        >
          {isError ? (get(errors, name) as string) : helperText || ''}
        </Typography>
      </Optional>
    </Stack>
  );
};

export default CheckboxInput;
